import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { getApiBaseUrl } from '../../utils/apiConfig';

export interface UserSettingsDto {
  account: {
    profileName: string;
    email: string;
    phone: string;
  };
  security: {
    twoFactorAuth: boolean;
    biometricLogin: boolean;
    loginAlerts: boolean;
  };
  notifications: {
    emailAlerts: boolean;
    smsAlerts: boolean;
    pushNotifications: boolean;
    marketingEmails: boolean;
  };
  appearance: {
    darkMode: boolean;
    accentColor: string;
    fontSize: number;
  };
  privacy: {
    canDownloadData: boolean;
    canDeleteAccount: boolean;
  };
  bankingPreferences: {
    defaultCurrency: string;
    language: string;
    defaultAccount: string;
  };
  devices: {
    name: string;
    location: string;
    lastActive: string;
    current: boolean;
    id?: number;
  }[];
}

interface UserSettingsState {
  settings: UserSettingsDto | null;
  isLoading: boolean;
  error: string | null;
  saveSuccess: boolean;
}

const initialState: UserSettingsState = {
  settings: null,
  isLoading: false,
  error: null,
  saveSuccess: false,
};

export const fetchSettings = createAsyncThunk<UserSettingsDto, void, { state: RootState }>(
  'userSettings/fetchSettings',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${getApiBaseUrl()}/settings`, {
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
      });
      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to fetch settings');
      }
      return json.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

export const updateSettings = createAsyncThunk<UserSettingsDto, UserSettingsDto, { state: RootState }>(
  'userSettings/updateSettings',
  async (settings, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${getApiBaseUrl()}/settings`, {
        method: 'PUT',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
        body: JSON.stringify(settings),
      });
      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to update settings');
      }
      return json.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

const userSettingsSlice = createSlice({
  name: 'userSettings',
  initialState,
  reducers: {
    clearSettingsSuccess: (state) => {
      state.saveSuccess = false;
    },
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action: PayloadAction<UserSettingsDto>) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.saveSuccess = false;
      })
      .addCase(updateSettings.fulfilled, (state, action: PayloadAction<UserSettingsDto>) => {
        state.isLoading = false;
        state.settings = action.payload;
        state.saveSuccess = true;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.saveSuccess = false;
      });
  },
});

export const { clearSettingsSuccess, clearSettingsError } = userSettingsSlice.actions;
export default userSettingsSlice.reducer; 